import axios from 'axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiContext } from '@trg/api';
import {
  useInfoAlertStore,
  useLayoutStore,
  useLoadingStore,
  useSnackbarStore,
} from '../../layout';
import { useRefreshData } from '../hooks';
import { request } from '../utils';
import type {
  AddCartApiParams,
  AddCartApiResponse,
  MiniCartApiResponse,
} from './cart-api.type';

export const miniCartApiPath = '/cart/miniCartList';
export const addCartApiPath = '/cart/add';

export function useMiniCartApi() {
  const { host } = useApiContext();
  if (host === undefined) throw new Error('ApiContext not found');

  return useQuery({
    queryKey: ['cart', 'miniCart'],
    queryFn: async () => {
      const { data } = await axios.get<string>(host + miniCartApiPath);
      return JSON.parse(data) as MiniCartApiResponse;
    },
  });
}

export interface AddCartApiOptions {
  onSuccess?: (response: AddCartApiResponse) => void;
  onError?: (error: { message: string }) => void;
}

export function useAddToCartApi(options?: AddCartApiOptions) {
  const showLoading = useLoadingStore.use.showLoading();
  const closeLoading = useLoadingStore.use.closeLoading();
  const openSnackbar = useSnackbarStore.use.openSnackbar();
  const openInfoAlert = useInfoAlertStore.use.openInfoAlert();
  const triggerAddingToCart = useLayoutStore.use.triggerAddingToCart();

  const refreshMiniCartApi = useRefreshData(['cart', 'miniCart']);

  return useMutation({
    mutationKey: ['cart', 'add'],
    mutationFn: async (params: Omit<AddCartApiParams, 'CSRFToken'>) => {
      const response = await request<string>({
        method: 'post',
        url: addCartApiPath,
        data: params,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        devHost: true,
        token: true,
        onStart: showLoading,
        onEnd: closeLoading,
      });
      return JSON.parse(response) as AddCartApiResponse;
    },
    onSuccess(response) {
      if (response.rtnCode === 1) {
        openSnackbar('已成功加入購物車');
        triggerAddingToCart();
        refreshMiniCartApi();
        options?.onSuccess?.(response);
      } else {
        openInfoAlert({ message: response.rtnMsg });
        options?.onError?.({ message: response.rtnMsg });
      }
    },
    onError(error: Error) {
      openInfoAlert({ message: error.message });
      options?.onError?.(error);
    },
  });
}
